/**
 * RecoveryCoachCard — AI-driven recovery prescription (status, actions, targets)
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Activity, Loader2, RefreshCw, Heart, Moon, Droplets, ChevronRight, Sparkles, AlertTriangle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS = {
  green: { label: "READY", color: "#10b981", icon: Activity, bg: "from-emerald-500/20 to-emerald-900/5", border: "border-emerald-500/40" },
  yellow: { label: "CAUTION", color: "#FFD700", icon: AlertTriangle, bg: "from-amber-500/20 to-amber-900/5", border: "border-amber-500/40" },
  red: { label: "REST", color: "#ef4444", icon: AlertTriangle, bg: "from-red-500/20 to-red-900/5", border: "border-red-500/40" },
};

const RECOMMEND_TEXT = {
  push: "Push hard tomorrow — you're in the green",
  taper: "Taper intensity tomorrow — listen to your body",
  rest: "Take a full rest day tomorrow — your body needs it",
};

const RecoveryCoachCard = () => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const fetchPlan = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/recovery-coach`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlan(res.data.plan);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load recovery plan");
    } finally {
      setLoading(false);
    }
  };

  const generate = async (force = false) => {
    setGenerating(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/recovery-coach/generate${force ? "?force=true" : ""}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      setPlan(res.data.plan);
    } catch (e) {
      setError(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchPlan();
  }, []);

  if (loading) {
    return (
      <div className="bg-card border border-border p-6 flex items-center justify-center rounded-lg" data-testid="recovery-coach-loading">
        <Loader2 className="w-5 h-5 text-orange-400 animate-spin" />
      </div>
    );
  }

  if (!plan) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-orange-500/10 to-zinc-900/10 border border-orange-500/30 p-6 rounded-lg text-center space-y-4"
        data-testid="recovery-coach-empty"
      >
        <div className="inline-flex items-center justify-center w-12 h-12 bg-orange-500/10 border border-orange-500/40 rounded">
          <Heart className="w-6 h-6 text-orange-400" />
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-orange-400 mb-1">
            AI Recovery Coach
          </p>
          <h3 className="text-2xl font-bold text-white">Get Your Recovery Plan</h3>
          <p className="text-sm text-zinc-400 mt-2">
            Personalized recovery prescription based on your last 7 days of sleep, soreness, and training.
          </p>
        </div>
        <button
          onClick={() => generate(false)}
          disabled={generating}
          className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white px-6 py-3 font-black uppercase tracking-wider text-xs inline-flex items-center gap-2 rounded"
          data-testid="generate-recovery-plan-btn"
        >
          {generating ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</>
          ) : (
            <><Sparkles className="w-4 h-4" /> Diagnose My Recovery</>
          )}
        </button>
        {error && <p className="text-xs text-red-400 font-mono">{error}</p>}
      </motion.div>
    );
  }

  const statusConfig = STATUS[plan.status] || STATUS.green;
  const StatusIcon = statusConfig.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-br ${statusConfig.bg} border ${statusConfig.border} p-5 rounded-lg space-y-4`}
      data-testid="recovery-coach-card"
    >
      {/* Header with status badge */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded flex items-center justify-center"
            style={{ background: `${statusConfig.color}22`, border: `1px solid ${statusConfig.color}66` }}
          >
            <StatusIcon className="w-6 h-6" style={{ color: statusConfig.color }} />
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-400">
              Recovery Status
            </p>
            <p className="text-2xl font-black uppercase" style={{ color: statusConfig.color }} data-testid="recovery-status-label">
              {statusConfig.label}
            </p>
          </div>
        </div>
        <button
          onClick={() => generate(true)}
          disabled={generating}
          className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-400 hover:text-orange-400 inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="regenerate-recovery-btn"
        >
          {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
      </div>

      <p className="text-sm text-white leading-relaxed" data-testid="recovery-status-msg">
        {plan.status_message}
      </p>

      {/* Tomorrow recommendation */}
      <div className="p-3 bg-black/30 border border-white/[0.06] rounded">
        <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-400 mb-1">
          Tomorrow
        </p>
        <p className="text-sm font-bold text-white" data-testid="recovery-tomorrow-rec">
          {RECOMMEND_TEXT[plan.tomorrow_recommendation] || plan.tomorrow_recommendation}
        </p>
      </div>

      {/* Action items */}
      {plan.actions && plan.actions.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-400">
            Action Plan
          </p>
          <div className="space-y-1.5">
            {plan.actions.map((a, i) => (
              <div
                key={i}
                className="flex items-start gap-2 p-2.5 bg-black/30 border border-white/[0.06] rounded"
                data-testid={`recovery-action-${i}`}
              >
                <ChevronRight className="w-3.5 h-3.5 text-orange-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-white">{a.action}</p>
                  <p className="text-xs text-zinc-400 mt-0.5">{a.detail}</p>
                </div>
                {a.duration_min && (
                  <span className="text-[10px] font-mono text-zinc-500 whitespace-nowrap">
                    {a.duration_min}m
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Targets row */}
      <div className="grid grid-cols-2 gap-2">
        {plan.sleep_target_hours && (
          <div className="p-3 bg-black/30 border border-white/[0.06] rounded">
            <div className="flex items-center gap-1.5 mb-1">
              <Moon className="w-3 h-3 text-blue-400" />
              <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Sleep Target</p>
            </div>
            <p className="text-lg font-black text-white" data-testid="recovery-sleep-target">
              {plan.sleep_target_hours}<span className="text-xs text-zinc-500 ml-1">hrs</span>
            </p>
          </div>
        )}
        {plan.hydration_target_oz && (
          <div className="p-3 bg-black/30 border border-white/[0.06] rounded">
            <div className="flex items-center gap-1.5 mb-1">
              <Droplets className="w-3 h-3 text-cyan-400" />
              <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Hydration</p>
            </div>
            <p className="text-lg font-black text-white" data-testid="recovery-hydration-target">
              {plan.hydration_target_oz}<span className="text-xs text-zinc-500 ml-1">oz</span>
            </p>
          </div>
        )}
      </div>

      {/* Focus areas */}
      {plan.focus_areas && plan.focus_areas.length > 0 && (
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-400 mb-1.5">
            Focus Areas
          </p>
          <div className="flex flex-wrap gap-1.5">
            {plan.focus_areas.map((f) => (
              <span
                key={f}
                className="text-[10px] font-mono uppercase tracking-widest bg-orange-500/15 border border-orange-500/30 text-orange-300 px-2 py-1 rounded"
              >
                {f}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Motivational */}
      {plan.motivational_note && (
        <div className="border-t border-white/[0.06] pt-3">
          <p className="text-sm italic text-zinc-300" data-testid="recovery-motivational">"{plan.motivational_note}"</p>
        </div>
      )}
    </motion.div>
  );
};

export default RecoveryCoachCard;
